'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MarginCalculator() {
  const t = useTranslations("tools.marginCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('profit');

  // Profit Margin Calculator State
  const [profitInputs, setProfitInputs] = useState({
    cost: '',
    revenue: '',
    margin: '',
    profit: ''
  });
  const [profitResults, setProfitResults] = useState<any>(null);

  // Stock Trading Margin State
  const [stockInputs, setStockInputs] = useState({
    price: '',
    shares: '',
    marginReq: '30'
  });
  const [stockResults, setStockResults] = useState<any>(null);

  // Currency Exchange Margin State
  const [currencyInputs, setCurrencyInputs] = useState({
    rate: '',
    ratio: '20',
    units: ''
  });
  const [currencyResults, setCurrencyResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#10b981',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      { background: colors.background, borderRadius: '1rem', marginBottom: '2rem' },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabs: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      {}
    ),
    tabBtn: getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        background: 'white',
        border: '2px solid #e2e8f0',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: colors.textSecondary,
        transition: 'all 0.3s ease',
      },
      { width: '100%' },
      { width: 'auto' },
      {}
    ),
    tabBtnActive: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      borderColor: 'transparent',
    },
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#334155',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    inputWrapper: {
      position: 'relative' as const,
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        padding: '0.75rem 1rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      {},
      { padding: '0.875rem 1.25rem' },
      {}
    ),
    inputWithPrefix: {
      paddingLeft: '2rem',
    },
    inputWithSuffix: {
      paddingRight: '2.5rem',
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 600,
    },
    inputSuffix: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontWeight: 600,
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      cursor: 'pointer',
    },
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginTop: '2rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      {}
    ),
    calculateBtn: getResponsiveStyle(
      {
        flex: 1,
        padding: '0.875rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
      },
      {},
      {},
      {}
    ),
    clearBtn: {
      flex: 1,
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
    },
    resultItemPrimary: {
      background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
      padding: '1.25rem',
      borderLeft: `4px solid ${colors.primary}`,
    },
    resultLabel: {
      color: colors.textSecondary,
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    resultValue: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    resultValuePrimary: getResponsiveStyle(
      { color: colors.primary, fontWeight: 700 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.75rem' }
    ),
    chartContainer: {
      marginTop: '2rem',
      textAlign: 'center' as const,
    },
    chartLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      marginTop: '1rem',
      flexWrap: 'wrap' as const,
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    legendColor: {
      width: '1rem',
      height: '1rem',
      borderRadius: '0.25rem',
    },
    legendText: {
      color: '#475569',
      fontSize: '0.9rem',
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: getResponsiveStyle(
      { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    infoSection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        marginTop: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    h4: {
      color: colors.textPrimary,
      marginBottom: '1rem',
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    infoItem: {
      marginBottom: '0.75rem',
      color: '#475569',
      lineHeight: 1.6,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const handleProfitCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const cost = parseFloat(profitInputs.cost) || 0;
    const revenue = parseFloat(profitInputs.revenue) || 0;
    const margin = parseFloat(profitInputs.margin) || null;
    const profit = parseFloat(profitInputs.profit) || null;

    let calculatedCost = cost;
    let calculatedRevenue = revenue;
    let calculatedProfit = profit;
    let calculatedMargin = margin;

    const filledCount = [cost > 0, revenue > 0, margin !== null, profit !== null].filter(Boolean).length;

    if (filledCount < 2) {
      alert(t('alertMinimumValues'));
      return;
    }

    if (cost > 0 && revenue > 0) {
      calculatedProfit = revenue - cost;
      calculatedMargin = (calculatedProfit / revenue) * 100;
    } else if (cost > 0 && margin !== null) {
      calculatedRevenue = cost / (1 - margin / 100);
      calculatedProfit = calculatedRevenue - cost;
    } else if (cost > 0 && profit !== null) {
      calculatedRevenue = cost + profit;
      calculatedMargin = (profit / calculatedRevenue) * 100;
    } else if (revenue > 0 && margin !== null) {
      calculatedProfit = revenue * (margin / 100);
      calculatedCost = revenue - calculatedProfit;
    } else if (revenue > 0 && profit !== null) {
      calculatedCost = revenue - profit;
      calculatedMargin = (profit / revenue) * 100;
    } else if (margin !== null && profit !== null) {
      calculatedRevenue = profit / (margin / 100);
      calculatedCost = calculatedRevenue - profit;
    }

    const markup = calculatedCost > 0 ? ((calculatedProfit ?? 0) / calculatedCost) * 100 : 0;

    setProfitResults({
      margin: calculatedMargin,
      profit: calculatedProfit,
      markup: markup,
      cost: calculatedCost,
      revenue: calculatedRevenue,
    });
  };

  const handleStockCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const price = parseFloat(stockInputs.price);
    const shares = parseFloat(stockInputs.shares);
    const marginReq = parseFloat(stockInputs.marginReq);

    if (!price || !shares || !marginReq) {
      alert(t('alertFillAllFields'));
      return;
    }

    const totalValue = price * shares;
    const amountRequired = totalValue * (marginReq / 100);
    const borrowedAmount = totalValue - amountRequired;

    setStockResults({
      amountRequired,
      totalValue,
      borrowedAmount,
    });
  };

  const handleCurrencyCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const rate = parseFloat(currencyInputs.rate);
    const ratio = parseFloat(currencyInputs.ratio);
    const units = parseFloat(currencyInputs.units);

    if (!rate || !ratio || !units) {
      alert(t('alertFillAllFields'));
      return;
    }

    const totalValue = rate * units;
    const amountRequired = totalValue / ratio;

    setCurrencyResults({
      amountRequired,
      totalValue,
      leverage: ratio,
    });
  };

  const clearProfitForm = () => {
    setProfitInputs({ cost: '', revenue: '', margin: '', profit: '' });
    setProfitResults(null);
  };

  const clearStockForm = () => {
    setStockInputs({ price: '', shares: '', marginReq: '30' });
    setStockResults(null);
  };

  const clearCurrencyForm = () => {
    setCurrencyInputs({ rate: '', ratio: '20', units: '' });
    setCurrencyResults(null);
  };

  const drawPieChart = (cost: number, profit: number) => {
    const total = cost + profit;
    const costPercent = (cost / total) * 100;
    const profitPercent = (profit / total) * 100;

    const centerX = 100;
    const centerY = 100;
    const radius = 70;

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    };

    const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return [
        "M", x, y,
        "L", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "Z"
      ].join(" ");
    };

    const costAngle = (costPercent / 100) * 360;

    return (
      <svg width="200" height="200" style={{ display: 'inline-block', margin: '1rem 0' }}>
        <path d={describeArc(centerX, centerY, radius, 0, costAngle)} fill={colors.primary} />
        <path d={describeArc(centerX, centerY, radius, costAngle, 360)} fill={colors.accent} />
        <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="white" />
        <text
          x={polarToCartesian(centerX, centerY, radius * 0.6, costAngle / 2).x}
          y={polarToCartesian(centerX, centerY, radius * 0.6, costAngle / 2).y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={colors.textPrimary}
          fontWeight="bold"
          fontSize="14"
        >
          {costPercent.toFixed(0)}%
        </text>
        <text
          x={polarToCartesian(centerX, centerY, radius * 0.6, costAngle + (360 - costAngle) / 2).x}
          y={polarToCartesian(centerX, centerY, radius * 0.6, costAngle + (360 - costAngle) / 2).y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={colors.textPrimary}
          fontWeight="bold"
          fontSize="14"
        >
          {profitPercent.toFixed(0)}%
        </text>
      </svg>
    );
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>🧮 {t('toolTitle')}</h2>
          </div>

          <div style={styles.tabs}>
            <button
              style={{...styles.tabBtn, ...(activeTab === 'profit' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('profit')}
            >
              {t('tabProfit')}
            </button>
            <button
              style={{...styles.tabBtn, ...(activeTab === 'stock' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('stock')}
            >
              {t('tabStock')}
            </button>
            <button
              style={{...styles.tabBtn, ...(activeTab === 'currency' ? styles.tabBtnActive : {})}}
              onClick={() => setActiveTab('currency')}
            >
              {t('tabCurrency')}
            </button>
          </div>

          {activeTab === 'profit' && (
            <>
              <div style={styles.calculatorGrid}>
                <div style={styles.calculatorForm}>
                  <form onSubmit={handleProfitCalculate}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('profitCostLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="120.00"
                          value={profitInputs.cost}
                          onChange={(e) => setProfitInputs({...profitInputs, cost: e.target.value})}
                          style={{...styles.input, ...styles.inputWithPrefix}}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('profitRevenueLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="160.00"
                          value={profitInputs.revenue}
                          onChange={(e) => setProfitInputs({...profitInputs, revenue: e.target.value})}
                          style={{...styles.input, ...styles.inputWithPrefix}}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('profitMarginLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <input
                          type="number"
                          step="0.01"
                          placeholder={t('profitMarginPlaceholder')}
                          value={profitInputs.margin}
                          onChange={(e) => setProfitInputs({...profitInputs, margin: e.target.value})}
                          style={{...styles.input, ...styles.inputWithSuffix}}
                        />
                        <span style={styles.inputSuffix}>%</span>
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('profitProfitLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder={t('profitProfitPlaceholder')}
                          value={profitInputs.profit}
                          onChange={(e) => setProfitInputs({...profitInputs, profit: e.target.value})}
                          style={{...styles.input, ...styles.inputWithPrefix}}
                        />
                      </div>
                    </div>

                    <div style={styles.buttonGroup}>
                      <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                      <button type="button" style={styles.clearBtn} onClick={clearProfitForm}>{t('clearButton')}</button>
                    </div>
                  </form>
                </div>

                {profitResults && (
                  <div style={styles.results}>
                    <h3 style={styles.h2}>{t('resultsTitle')}</h3>
                    <div style={{...styles.resultItem, ...styles.resultItemPrimary}}>
                      <span style={styles.resultLabel}>{t('profitMarginResult')}</span>
                      <span style={styles.resultValuePrimary}>{profitResults.margin.toFixed(2)}%</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('profitProfitResult')}</span>
                      <span style={styles.resultValue}>${profitResults.profit.toFixed(2)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('profitMarkupResult')}</span>
                      <span style={styles.resultValue}>{profitResults.markup.toFixed(2)}%</span>
                    </div>

                    <div style={styles.chartContainer}>
                      {drawPieChart(profitResults.cost, profitResults.profit)}
                      <div style={styles.chartLegend}>
                        <div style={styles.legendItem}>
                          <div style={{...styles.legendColor, background: colors.primary}}></div>
                          <span style={styles.legendText}>{t('chartCostLabel')}</span>
                        </div>
                        <div style={styles.legendItem}>
                          <div style={{...styles.legendColor, background: colors.accent}}></div>
                          <span style={styles.legendText}>{t('chartProfitLabel')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.infoSection}>
                <h4 style={styles.h4}>{t('profitTermsTitle')}</h4>
                <div style={styles.infoItem}><strong>{t('profitCostTerm')}</strong> {t('profitCostDesc')}</div>
                <div style={styles.infoItem}><strong>{t('profitRevenueTerm')}</strong> {t('profitRevenueDesc')}</div>
                <div style={styles.infoItem}><strong>{t('profitProfitTerm')}</strong> {t('profitProfitDesc')}</div>
                <div style={styles.infoItem}><strong>{t('profitMarginTerm')}</strong> {t('profitMarginDesc')}</div>
                <div style={styles.infoItem}><strong>{t('profitMarkupTerm')}</strong> {t('profitMarkupDesc')}</div>
              </div>
            </>
          )}

          {activeTab === 'stock' && (
            <>
              <div style={styles.calculatorGrid}>
                <div style={styles.calculatorForm}>
                  <form onSubmit={handleStockCalculate}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('stockPriceLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <span style={styles.inputPrefix}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="18.30"
                          value={stockInputs.price}
                          onChange={(e) => setStockInputs({...stockInputs, price: e.target.value})}
                          style={{...styles.input, ...styles.inputWithPrefix}}
                          required
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('stockSharesLabel')}</label>
                      <input
                        type="number"
                        step="1"
                        placeholder="100"
                        value={stockInputs.shares}
                        onChange={(e) => setStockInputs({...stockInputs, shares: e.target.value})}
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('stockMarginReqLabel')}</label>
                      <div style={styles.inputWrapper}>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="30"
                          value={stockInputs.marginReq}
                          onChange={(e) => setStockInputs({...stockInputs, marginReq: e.target.value})}
                          style={{...styles.input, ...styles.inputWithSuffix}}
                          required
                        />
                        <span style={styles.inputSuffix}>%</span>
                      </div>
                    </div>

                    <div style={styles.buttonGroup}>
                      <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                      <button type="button" style={styles.clearBtn} onClick={clearStockForm}>{t('clearButton')}</button>
                    </div>
                  </form>
                </div>

                {stockResults && (
                  <div style={styles.results}>
                    <h3 style={styles.h2}>{t('resultsTitle')}</h3>
                    <div style={{...styles.resultItem, ...styles.resultItemPrimary}}>
                      <span style={styles.resultLabel}>{t('stockAmountResult')}</span>
                      <span style={styles.resultValuePrimary}>${stockResults.amountRequired.toFixed(2)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('stockTotalResult')}</span>
                      <span style={styles.resultValue}>${stockResults.totalValue.toFixed(2)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('stockBorrowedResult')}</span>
                      <span style={styles.resultValue}>${stockResults.borrowedAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.infoSection}>
                <h4 style={styles.h4}>{t('stockTermsTitle')}</h4>
                <div style={styles.infoItem}><strong>{t('stockPriceTerm')}</strong> {t('stockPriceDesc')}</div>
                <div style={styles.infoItem}><strong>{t('stockSharesTerm')}</strong> {t('stockSharesDesc')}</div>
                <div style={styles.infoItem}><strong>{t('stockMarginReqTerm')}</strong> {t('stockMarginReqDesc')}</div>
                <div style={styles.infoItem}><strong>{t('stockAmountTerm')}</strong> {t('stockAmountDesc')}</div>
              </div>
            </>
          )}

          {activeTab === 'currency' && (
            <>
              <div style={styles.calculatorGrid}>
                <div style={styles.calculatorForm}>
                  <form onSubmit={handleCurrencyCalculate}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('currencyRateLabel')}</label>
                      <input
                        type="number"
                        step="0.0001"
                        placeholder="1.3000"
                        value={currencyInputs.rate}
                        onChange={(e) => setCurrencyInputs({...currencyInputs, rate: e.target.value})}
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('currencyRatioLabel')}</label>
                      <select
                        value={currencyInputs.ratio}
                        onChange={(e) => setCurrencyInputs({...currencyInputs, ratio: e.target.value})}
                        style={styles.select}
                        required
                      >
                        <option value="1">1:1</option>
                        <option value="5">5:1</option>
                        <option value="10">10:1</option>
                        <option value="20">20:1</option>
                        <option value="25">25:1</option>
                        <option value="30">30:1</option>
                        <option value="40">40:1</option>
                        <option value="50">50:1</option>
                      </select>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t('currencyUnitsLabel')}</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="100"
                        value={currencyInputs.units}
                        onChange={(e) => setCurrencyInputs({...currencyInputs, units: e.target.value})}
                        style={styles.input}
                        required
                      />
                    </div>

                    <div style={styles.buttonGroup}>
                      <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                      <button type="button" style={styles.clearBtn} onClick={clearCurrencyForm}>{t('clearButton')}</button>
                    </div>
                  </form>
                </div>

                {currencyResults && (
                  <div style={styles.results}>
                    <h3 style={styles.h2}>{t('resultsTitle')}</h3>
                    <div style={{...styles.resultItem, ...styles.resultItemPrimary}}>
                      <span style={styles.resultLabel}>{t('currencyAmountResult')}</span>
                      <span style={styles.resultValuePrimary}>{currencyResults.amountRequired.toFixed(3)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('currencyTotalResult')}</span>
                      <span style={styles.resultValue}>{currencyResults.totalValue.toFixed(2)}</span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t('currencyLeverageResult')}</span>
                      <span style={styles.resultValue}>{currencyResults.leverage}:1</span>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.infoSection}>
                <h4 style={styles.h4}>{t('currencyTermsTitle')}</h4>
                <div style={styles.infoItem}><strong>{t('currencyRateTerm')}</strong> {t('currencyRateDesc')}</div>
                <div style={styles.infoItem}><strong>{t('currencyRatioTerm')}</strong> {t('currencyRatioDesc')}</div>
                <div style={styles.infoItem}><strong>{t('currencyUnitsTerm')}</strong> {t('currencyUnitsDesc')}</div>
                <div style={styles.infoItem}><strong>{t('currencyAmountTerm')}</strong> {t('currencyAmountDesc')}</div>
              </div>
            </>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsPara1')}</p>
        <p style={styles.paragraph}>{t('insightsPara2')}</p>
        <p style={styles.paragraph}>{t('insightsPara3')}</p>
        <p style={styles.paragraph}>{t('insightsPara4')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{...styles.h4, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('detailsGrossTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsGrossPara')}</p>

        <h3 style={{...styles.h4, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('detailsOperatingTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsOperatingPara')}</p>

        <h3 style={{...styles.h4, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('detailsNetTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsNetPara')}</p>

        <h3 style={{...styles.h4, fontSize: '1.2rem', marginTop: '1.5rem'}}>{t('detailsMarkupTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsMarkupPara')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://www.sage.com/en-us/blog/margin-calculator/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.calculator.net/margin-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.sdocpa.com/how-to-calculate-profit-margin/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.xero.com/us/guides/what-is-profit-margin/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.omnicalculator.com/finance/margin" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref5')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.netsuite.com/portal/resource/articles/financial-management/profit-margin.shtml" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}
